import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage';
import { RootState } from '../store';

export interface EstablishmentActivityType {
    id: number;
    codigo: 'AGRO' | 'IND_ADM' | 'CONST';
    descripcion: string;
}

export type EstablishmentOriginCode = 'PROPIO' | 'TERCERO';

export interface EstablishmentOriginType {
    id: number;
    codigo: EstablishmentOriginCode;
    descripcion: string;
}
interface EstablishmentState {
    selectedEstablishmentId: number | null;
    selectedEstablishmentIdFilter: number | null;
    selectedEstablishmentName: string | null;
    selectedEstablishmentIdOrganismId: number | null;
    establishmentActivityTypes: EstablishmentActivityType[];
    establishmentOriginTypes: EstablishmentOriginType[];
}

const initialState: EstablishmentState = {
    selectedEstablishmentId: null,
    selectedEstablishmentIdFilter: null,
    selectedEstablishmentName: null,
    selectedEstablishmentIdOrganismId: null,
    establishmentActivityTypes: [],
    establishmentOriginTypes: [
        { id: 1, codigo: 'PROPIO', descripcion: 'Propio' },
        { id: 2, codigo: 'TERCERO', descripcion: 'Tercero' }
    ]
};

const establishmentSlice = createSlice({
    name: 'establishment',
    initialState,
    reducers: {
        setEstablishmentId: (state, action: PayloadAction<number | null>) => {
            state.selectedEstablishmentId = action.payload;
        },
        setEstablishmentOrganismId: (state, action: PayloadAction<number | null>) => {
            state.selectedEstablishmentIdOrganismId = action.payload;
        },
        setEstablishmentIdFilter: (state, action: PayloadAction<number | null>) => {
            state.selectedEstablishmentIdFilter = action.payload;
        },
        setEstablishmentName: (state, action: PayloadAction<string | null>) => {
            state.selectedEstablishmentName = action.payload;
        },
        clearEstablishmentId: (state) => {
            state.selectedEstablishmentId = null;
        },
        clearEstablishmentIdFilter: (state) => {
            state.selectedEstablishmentIdFilter = null;
        },
        clearEstablishmentName: (state) => {
            state.selectedEstablishmentName = null;
        },
        setEstablihsmentActivityTypes: (
            state,
            action: PayloadAction<EstablishmentActivityType[]>
        ) => {
            state.establishmentActivityTypes = action.payload;
        },
        clearActivityTypes: (state) => {
            state.establishmentActivityTypes = [];
        },
        setEstablishmentOriginTypes: (state, action: PayloadAction<EstablishmentOriginType[]>) => {
            state.establishmentOriginTypes = action.payload;
        },
        clearOriginTypes: (state) => {
            state.establishmentOriginTypes = [];
        }
    }
});

export const {
    setEstablishmentId,
    clearEstablishmentId,
    setEstablishmentIdFilter,
    setEstablishmentName,
    clearEstablishmentName,
    clearEstablishmentIdFilter,
    setEstablishmentOrganismId,
    setEstablihsmentActivityTypes,
    clearActivityTypes
} = establishmentSlice.actions;
const persistConfig = {
    key: 'establishment',
    storage
};

export default persistReducer(persistConfig, establishmentSlice.reducer);

export const selectEstablishmentState = (state: RootState) => state.establishment;

export const selectEstablishmentActivityTypes = (state: RootState): EstablishmentActivityType[] =>
    selectEstablishmentState(state).establishmentActivityTypes;

export const selectActivityTypeById =
    (id?: number | null): ((state: RootState) => EstablishmentActivityType | null) =>
    (state: RootState): EstablishmentActivityType | null => {
        if (!id) return null;
        return selectEstablishmentActivityTypes(state).find((t) => t.id === id) ?? null;
    };

export const selectActivityTypeByCode =
    (
        code?: EstablishmentActivityType['codigo'] | null
    ): ((state: RootState) => EstablishmentActivityType | null) =>
    (state: RootState): EstablishmentActivityType | null => {
        if (!code) return null;
        return selectEstablishmentActivityTypes(state).find((t) => t.codigo === code) ?? null;
    };

export const selectEstablishmentOriginTypes = (state: RootState): EstablishmentOriginType[] =>
    selectEstablishmentState(state).establishmentOriginTypes;

export const selectOriginTypeById =
    (id?: number | null): ((state: RootState) => EstablishmentOriginType | null) =>
    (state: RootState): EstablishmentOriginType | null => {
        if (!id) return null;
        return selectEstablishmentOriginTypes(state).find((t) => t.id === id) ?? null;
    };
